import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { InputAdornment, TextField } from '@mui/material'
import React from 'react'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { DUE_DATES_HELP_URL, RamoMark } from '../common/due-date-common'
import { DUE_DATE_RAMOS, dueDateHint, useDueDateFormModel } from './due-date-form-model'

const daysAdornment = { input: { endAdornment: <InputAdornment position="end">días</InputAdornment> } }

const DueDateForm = () => {
    const model = useDueDateFormModel()
    const { form } = model

    const daysField = (parametroId: number, label: string) => (
        <TextField
            className="v2-field" variant="filled" fullWidth required
            label={label}
            value={form[parametroId] ?? ''}
            helperText={dueDateHint(form[parametroId] ?? '')}
            onChange={(event) => model.change(parametroId, event.target.value)}
            onFocus={(event) => event.target.select()}
            slotProps={{ ...daysAdornment, htmlInput: { inputMode: 'numeric', style: { textAlign: 'right' } } }}
        />
    )

    return (
        <KarteraAdminTemplate loading={false} title="Fechas de vencimiento">
            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title="Fechas de vencimiento"
                    description="Días que se suman a la fecha de inicio de vigencia de cada recibo para calcular su vencimiento."
                    backTo={model.backTo}
                    backLabel="Configuración"
                    actions={(
                        <a
                            href={DUE_DATES_HELP_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-10 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        >
                            <HelpOutlineIcon style={{ fontSize: 18 }} />
                            Ayuda
                        </a>
                    )}
                />

                <div className="flex items-start gap-3 rounded-[14px] border border-k-line bg-k-sunken px-4 py-3.5 text-sm text-k-ink-2">
                    <InfoOutlinedIcon className="mt-px shrink-0 text-brand-indigo" style={{ fontSize: 20 }} />
                    <p className="m-0">Los números positivos agregan días a la fecha de vigencia; los negativos los restan. Usa 0 para que venza el mismo día.</p>
                </div>

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                {DUE_DATE_RAMOS.map((ramo) => (
                    <section key={ramo.id} className="k-card">
                        <div className="flex items-center gap-3">
                            <RamoMark ramo={ramo.ramo} boxed />
                            <h2 className="k-card-title">{ramo.label}</h2>
                        </div>
                        <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                            {daysField(ramo.primerReciboId, 'Primer recibo')}
                            {daysField(ramo.subsecuenteId, 'Recibos subsecuentes')}
                        </div>
                    </section>
                ))}

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.busy}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar configuración
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default DueDateForm
